import axios from 'axios';
import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseMarketHeatMap {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private selectedExchange: string = 'sp500';
    private selectedPeriod: string = '1D';

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }

        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        // Minimal widget: only the chart container remains here as requested.
        const widgetHtml = `
                        <style>
                            * {
                                box-sizing: border-box;
                            }
                            .chartDv {
                                position: relative;
                                overflow: hidden;
                            }
                            .js-plotly-plot, .plotly {
                                position: relative;
                            }
                            .plotly .modebar {
                                position: absolute;
                                top: 2px;
                                right: 2px;
                            }
                            .plotly .modebar-group {
                                display: inline-block;
                            }
                            .plotly .modebar-btn {
                                display: inline-block;
                                cursor: pointer;
                            }
                            svg {
                                overflow: hidden;
                            }
                        </style>
    <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
      <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Market Heatmap</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="heatmap" id="heatMapContainer" style="width:100%; height: 100%;" data-testing-id="heatmap-container"></div>
          </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);

            const plotlyStyles = document.createElement('style');
            plotlyStyles.textContent = `
                .plotly, .plotly div {
                    box-sizing: border-box;
                }
                .plotly .main-svg {
                    position: relative;
                }
                .svg-container {
                    position: relative;
                    width: 100%;
                    height: 100%;
                }
                .modebar {
                    position: absolute;
                    top: 0;
                    right: 0;
                    z-index: 1001;
                }
                .modebar-btn {
                    cursor: pointer;
                }
                .js-plotly-plot .plotly .main-svg{
                    position: absolute;
                    top: 0px;
                    left: 0px;
                    pointer-events: none;
                }
            `;
            this.shadowRoot.appendChild(plotlyStyles);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).Plotly) return resolve(true);
            const existing = document.querySelector('script[data-plotly-lib]');
            if (existing) {
                existing.addEventListener('load', () => resolve(true));
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://cdn.plot.ly/plotly-2.24.2.min.js';
            script.async = true;
            script.setAttribute('data-plotly-lib', 'true');
            document.head.appendChild(script);
            script.onload = () => resolve(true);
        });
    }

    private async setupChart(): Promise<void> {
        try {
            const response = await axios.get(
                `${APIURL}/v1/heat_map`,
                {
                    params: {
                        exchange: this.selectedExchange || 'NASDAQ',
                        period: this.selectedPeriod || '1D'
                    },
                    headers: {
                    }
                }
            );
    
            this.currentData = response.data || {};
            console.log("✅ Treemap API Data:", this.currentData);
    
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
    
        } catch (error) {
            console.error("❌ Error fetching treemap data:", error);
        }
    }

    private updateDisplay(): void {
        if (!this.currentData || !(window as any).Plotly) {
            setTimeout(() => this.updateDisplay(), 200);
            return;
        }
        this.renderTreemap();
    }

    private renderTreemap(): void {
        const container = this.shadowRoot.querySelector('#heatMapContainer');
        const heatmap = this.shadowRoot.querySelector('.heatmap') as HTMLElement;

if (!heatmap) return;

const hasData =
    Array.isArray(this.currentData?.data?.labels) &&
    this.currentData.data.labels.length > 0;

if (!hasData) {
    heatmap.removeAttribute('data-testing-id');
    heatmap.innerHTML = getNoDataHTML();
    return;
}

heatmap.setAttribute(
    'data-testing-id',
    'market-heatmap-content'
);
        // only include the Plotly treemap container here
        if (container) container.innerHTML = `<div id="treemapChart" style="width:100%; height:100%;"></div>`;
        this.drawTreemap();
    }

    private drawTreemap(): void {
        const Plotly = (window as any).Plotly;
        const container = this.shadowRoot.querySelector('#treemapChart') as HTMLElement;
        if (!container || !this.currentData || !Plotly) return;

        const api = this.currentData;
        const labels = api.data?.labels || [];
        const parents = api.data?.parents || [];
        const values = api.data?.values || [];
        const colors = api.colors || labels.map(() => '#262626');

        const max_value = Math.max(...values);

        const plotData = [
            {
                type: 'treemap',
                labels,
                parents,
                values,
                textinfo: 'label+percent',
                textposition: 'middle center',
                hoverinfo: 'label+percent',
                tiling: { orientation: 'v', packing: 'squarify', squarifyratio: 2 },
                texttemplate: '%{label}',
                textfont: { size: 16, color: 'white' },
                marker: {
                    colors: colors,
                    showscale: true,
                    colorscale: [
                        [0, '#f63538'],
                        [0.5, '#262626'],
                        [1, '#30cc5a'],
                    ],
                    cmin: -3,
                    cmax: 3,
                    colorbar: {
                        x: 1.0,
                        xpad: 8,
                        y: 0.5,
                        ypad: 10,
                        len: 0.9,
                        thickness: 12,
                        outlinewidth: 0,
                        tickfont: { color: '#ffffff', size: 12, family: 'Arial' },
                        bordercolor: 'transparent',
                        bgcolor: 'rgba(0,0,0,0)',
                    },
                    line: {
                        width: labels.map((label: any) => label.includes('\n') ? 0.5 : 1),
                        color: labels.map((label: any) => label.includes('\n') ? 'rgba(63,63,63,0.4)' : 'rgba(63,63,63,0.1)'),
                    },
                },
            }
        ];

        const chartHeight = Math.max(container.clientHeight || 0, 300);

        const layout: any = {
            autosize: true,
            height: chartHeight,
            margin: { b: 0, l: 0, r: 48, t: 8 },
            paper_bgcolor: 'transparent',
            plot_bgcolor: 'transparent',
        };

        // Hide Plotly modebar — it overlays the colorbar (Y-scale) at the top-right.
        const config: any = {
            responsive: true,
            displayModeBar: false,
            displaylogo: false,
            staticPlot: false,
        };

        try { Plotly.purge(container); } catch (e) { }

        Plotly.newPlot(container, plotData, layout, config).then(() => {
            // Add data-testing-id to treemap elements after render
            setTimeout(() => {
                const treemapSvg = this.shadowRoot.querySelector('#treemapChart svg');
                if (treemapSvg) {
                    treemapSvg.setAttribute('data-testing-id', 'treemap-svg');
                }
                const treemapTiles = this.shadowRoot.querySelectorAll('.treemap .slice');
                treemapTiles.forEach((tile, idx) => {
                    tile.setAttribute('data-testing-id', `treemap-tile-${idx}`);
                    const tileText = tile.querySelector('.slice-text');
                    if (tileText) {
                        tileText.setAttribute('data-testing-id', `treemap-tile-text-${idx}`);
                    }
                });
                const colorbar = this.shadowRoot.querySelector('.colorbar');
                if (colorbar) {
                    colorbar.setAttribute('data-testing-id', 'treemap-colorbar');
                }
            }, 500);
        });
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.updateInterval) clearInterval(this.updateInterval);
        this.widgetResizeObserver?.disconnect();
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) handlers.forEach(h => h({ type, data }));
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketHeatMap: typeof TraderverseMarketHeatMap; } }
if (typeof window !== 'undefined') window.TraderverseMarketHeatMap = TraderverseMarketHeatMap;